"use client";

import { useMemo, useState } from "react";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  Notice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  CARD,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { useWordList } from "@/components/tools/word-games/use-word-list";
import { WordListStatus } from "@/components/tools/word-games/word-list-status";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type Blend,
  combine,
  parseCombiner,
} from "@/lib/tool-kit/word-games/word-combiner";

/** English strings. A translated content file overrides these through `ui`. */
export const WORD_COMBINER_STRINGS = {
  firstLabel: "First word or name",
  firstPlaceholder: "breakfast",
  secondLabel: "Second word or name",
  secondPlaceholder: "lunch",
  bothOrders: "Also blend in the other order",
  realOnly: "Show only blends that are already words",
  minLabel: "Fewest letters taken from a cut word",
  minOption: "one:{count} letter|other:{count} letters",
  tryLabel: "Try:",
  exampleFirsts: ["breakfast", "smoke", "motor", "Brad"],
  exampleSeconds: ["lunch", "fog", "hotel", "Angelina"],
  emptyPrompt: "Enter two words or names to see how they blend.",
  short: "Each word needs at least two letters.",
  same: "Enter two different words.",
  heading: "Blends",
  count: "one:{count} blend|other:{count} blends",
  ranking:
    "Best first: more shared letters at the join, a balanced length, and an easy-to-say sound rank higher.",
  sharedLetters: "one:{count} shared letter|other:{count} shared letters",
  recipe: "{head} from {first} + {tail} from {second}",
  realBadge: "Already a word",
  noReal:
    "None of these blends is in the word list yet, which is normal: most portmanteaus are new. Clear the filter to see them all.",
  loading: "Checking blends against the word list (once per visit)…",
  loadError:
    "Could not load the word list, so blends are not marked as real words. Check your connection and try again.",
  retry: "Try again",
  showMore: "Show more",
  copyAll: "Copy all",
  copied: "Copied!",
  note: "Blends are built from spelling, not sound: say each one aloud before you use it, and search for it in case it already means something. “Already a word” means the blend is in ENABLE, a public-domain word-game list; it has no newer coinages, names or brands.",
} as const;

type Strings = typeof WORD_COMBINER_STRINGS;

const MIN_PARTS = [1, 2, 3];
const PAGE = 10;
const RE_UPPER_START = /^\p{Lu}/u;
const INPUT_KEYS = { short: "short", same: "same" } as const;

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function BlendCard({
  blend,
  first,
  second,
  capital,
  real,
  strings,
}: {
  blend: Blend;
  first: string;
  second: string;
  capital: boolean;
  real: boolean;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const shared = blend.sharedHead + blend.sharedTail;
  const head = capital ? capitalize(blend.head) : blend.head;
  const [from, to] = blend.order === "ab" ? [first, second] : [second, first];
  const headSplit = blend.head.length - blend.sharedHead;
  return (
    <li className={`${CARD} space-y-1`}>
      <p className="font-semibold text-2xl tracking-wide">
        <span className="text-primary">
          {head.slice(0, headSplit)}
          <span className="underline decoration-2 underline-offset-4">
            {head.slice(headSplit)}
          </span>
        </span>
        <span className="text-amber-600 dark:text-amber-400">
          <span className="underline decoration-2 underline-offset-4">
            {blend.tail.slice(0, blend.sharedTail)}
          </span>
          {blend.tail.slice(blend.sharedTail)}
        </span>
      </p>
      <p className={HINT}>
        {t(strings.recipe, {
          head: blend.head,
          first: from,
          tail: blend.tail,
          second: to,
        })}
        {" · "}
        {tPlural(strings.sharedLetters, locale, shared, { count: fmt(shared) })}
      </p>
      {real ? (
        <p className="font-medium text-emerald-700 text-xs dark:text-emerald-400">
          {strings.realBadge}
        </p>
      ) : null}
    </li>
  );
}

export default function WordCombinerWidget() {
  const strings = useToolUi(WORD_COMBINER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [first, setFirst] = useState("");
  const [second, setSecond] = useState("");
  const [bothOrders, setBothOrders] = useState(true);
  const [minPart, setMinPart] = useState(2);
  const [shown, setShown] = useState(PAGE);
  const [realOnly, setRealOnly] = useState(false);
  const parsed = parseCombiner(first, second);
  const { words, status, retry } = useWordList(parsed.ok);
  const known = useMemo(() => (words ? new Set(words) : null), [words]);
  const all = useMemo(() => {
    const input = parseCombiner(first, second);
    return input.ok
      ? combine(input.first, input.second, { minPart, bothOrders })
      : null;
  }, [first, second, minPart, bothOrders]);
  const blends =
    all && realOnly && known ? all.filter((b) => known.has(b.word)) : all;
  const firstCapital = RE_UPPER_START.test(first.trim());
  const secondCapital = RE_UPPER_START.test(second.trim());

  function notice() {
    if (parsed.ok) {
      return null;
    }
    if (parsed.reason === "empty") {
      return <Notice>{strings.emptyPrompt}</Notice>;
    }
    return <ErrorNotice>{strings[INPUT_KEYS[parsed.reason]]}</ErrorNotice>;
  }

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="combiner-first"
          label={strings.firstLabel}
          onChange={(value) => {
            setFirst(value.slice(0, 40));
            setShown(PAGE);
          }}
          placeholder={strings.firstPlaceholder}
          value={first}
        />
        <TextField
          id="combiner-second"
          label={strings.secondLabel}
          onChange={(value) => {
            setSecond(value.slice(0, 40));
            setShown(PAGE);
          }}
          placeholder={strings.secondPlaceholder}
          value={second}
        />
      </div>
      <div className="grid items-end gap-4 sm:grid-cols-2">
        <SelectField
          id="combiner-min"
          label={strings.minLabel}
          onChange={(value) => setMinPart(Number(value))}
          options={MIN_PARTS.map((value) => ({
            value: String(value),
            label: tPlural(strings.minOption, locale, value, {
              count: fmt(value),
            }),
          }))}
          value={String(minPart)}
        />
        <div className="space-y-2">
          <Checkbox
            checked={bothOrders}
            label={strings.bothOrders}
            onChange={setBothOrders}
          />
          <Checkbox
            checked={realOnly}
            label={strings.realOnly}
            onChange={(value) => {
              setRealOnly(value);
              setShown(PAGE);
            }}
          />
        </div>
      </div>
      <p className="flex flex-wrap items-center gap-2 text-sm">
        <span className={HINT}>{strings.tryLabel}</span>
        {strings.exampleFirsts.map((word, index) => (
          <button
            className="font-medium text-primary underline-offset-4 hover:underline"
            key={word}
            onClick={() => {
              setFirst(word);
              setSecond(strings.exampleSeconds[index]);
              setShown(PAGE);
            }}
            type="button"
          >
            {word} + {strings.exampleSeconds[index]}
          </button>
        ))}
      </p>
      {notice()}
      <WordListStatus
        error={strings.loadError}
        loading={strings.loading}
        onRetry={retry}
        retry={strings.retry}
        status={status}
      />
      {parsed.ok && blends ? (
        <div aria-live="polite" className="space-y-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="space-y-1">
              <h2 className={WIDGET_H2}>{strings.heading}</h2>
              <p className={HINT}>
                {tPlural(strings.count, locale, blends.length, {
                  count: fmt(blends.length),
                })}
                {" · "}
                {strings.ranking}
              </p>
            </div>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyAll}
              small
              text={blends
                .map((blend) =>
                  (blend.order === "ab" ? firstCapital : secondCapital)
                    ? capitalize(blend.word)
                    : blend.word
                )
                .join("\n")}
            />
          </div>
          {blends.length === 0 ? <Notice>{strings.noReal}</Notice> : null}
          <ul className="grid gap-3 sm:grid-cols-2">
            {blends.slice(0, shown).map((blend) => (
              <BlendCard
                blend={blend}
                capital={blend.order === "ab" ? firstCapital : secondCapital}
                first={parsed.first}
                key={`${blend.order}-${blend.word}`}
                real={known?.has(blend.word) ?? false}
                second={parsed.second}
                strings={strings}
              />
            ))}
          </ul>
          {blends.length > shown ? (
            <button
              className={BUTTON_SMALL}
              onClick={() => setShown((count) => count + PAGE)}
              type="button"
            >
              {strings.showMore}
            </button>
          ) : null}
        </div>
      ) : null}
      <p className={HINT}>{strings.note}</p>
    </>
  );
}
